import clsx from 'classnames';
import ArrowDropRight from 'icons/lib/arrow_drop_right.svg';
import Link from 'next/link';
import { Fragment, MouseEvent, ReactNode } from 'react';

import { useTranslation } from '@/i18n/client';
import { MenuItem } from '@/shared/api/endpoints/menu';
import { Image } from '@/shared/components/image';
import { useCurrentLocale } from '@/shared/utils/client';

import styles from './MultilevelSidebar.module.scss';
import { CategoryPath, MultilevelSidebarProps } from './types';
import { categoryImages, imageBreakpoints } from './utils';

export const MultilevelSidebar = ({
  items,
  open,
  itemClass,
  showAll = false,
  showFirstLevelImage = true,
  onCloseButtonPress,
  setOpen,
}: MultilevelSidebarProps) => {
  const { t } = useTranslation('common');
  const locale = useCurrentLocale();

  const openNewLevel = (id: string) => {
    setOpen((prevValue) => {
      const newValue = [...prevValue];

      newValue.push(id);

      return newValue;
    });
  };

  const closeLevel = (id: string) => {
    setOpen((prevValue) => {
      const newValue = [...prevValue].filter((value) => value !== id);

      return newValue;
    });
  };

  const goToLevel = (id: string) => {
    setOpen((prevValue) => {
      const newValue = [...prevValue];
      const itemIndex = prevValue.indexOf(id) + 1;

      newValue.splice(itemIndex, newValue.length - itemIndex);

      return newValue;
    });
  };

  const linkClick = (event: MouseEvent, hasChildren: boolean, id: string) => {
    if (!hasChildren) {
      onCloseButtonPress?.();
      return;
    }

    event.preventDefault();
    openNewLevel(id);
  };

  const categoryLinkClick = (event: MouseEvent, id: string | null) => {
    event.preventDefault();

    if (!id) {
      return;
    }

    goToLevel(id);
  };

  const showMoreClick = () => {
    onCloseButtonPress?.();
  };

  const backClick = (event: MouseEvent, id: string) => {
    event.preventDefault();
    closeLevel(id);
  };

  const renderItem = (
    children: MenuItem[],
    depth = 0,
    index = 0,
    parentId = '',
    categoryPath: CategoryPath[] = []
  ): ReactNode => {
    const id = `${parentId}${depth}${index}`;

    return (
      <Fragment key={id}>
        <ul
          id={`menuList-${id}`}
          style={{
            visibility:
              depth === 0
                ? 'visible'
                : open.includes(id)
                  ? 'visible'
                  : 'hidden',
            transform:
              depth === 0
                ? 'translateX(0)'
                : open.includes(id)
                  ? 'translateX(0)'
                  : 'translateX(500px)',
          }}
          className={clsx(styles['multilevelSidebar--item'], {
            [styles['multilevelSidebar--nestedItem']]: depth !== 0,
            [itemClass || '']: !!itemClass,
          })}
        >
          {depth > 0 && (
            <>
              <Link
                prefetch={false}
                key="back"
                className={styles['multilevelSidebar--backLabel']}
                href="#"
                onClick={(event) => backClick(event, id)}
              >
                <li>
                  <ArrowDropRight />
                  <span>{t('common.back')}</span>
                </li>
              </Link>
              {categoryPath.map((category, index) => {
                if (index + 1 > depth) {
                  return null;
                }

                return (
                  <Link
                    key={category.id}
                    href={category?.url || '#'}
                    onClick={(event) =>
                      categoryLinkClick(
                        event,
                        categoryPath[index + 1]?.id ?? null
                      )
                    }
                  >
                    <li>
                      <span style={{ fontWeight: 'bold' }}>
                        {category.label}
                      </span>
                      {showAll && category.id === parentId && (
                        <Link
                          style={{ fontWeight: 'bold' }}
                          href={category?.url || '#'}
                          onClick={showMoreClick}
                        >
                          {t('common.showAll')}
                        </Link>
                      )}
                    </li>
                  </Link>
                );
              })}
            </>
          )}
          {children.map((child, i) => (
            <Link
              prefetch={false}
              href={child?.url || '#'}
              key={i}
              style={{ height: depth !== 0 ? '54.4px' : '63.38px' }}
              onClick={(event) =>
                linkClick(
                  event,
                  Array.isArray(child?.children) && child?.children?.length > 0,
                  `${id}${depth + 1}${i}`
                )
              }
            >
              {depth === 0 && showFirstLevelImage && (
                <div
                  className={styles['multilevelSidebar--itemImageRingWrapper']}
                >
                  <Image
                    alt={child.label}
                    title={child.label}
                    breakpoints={imageBreakpoints}
                    className={styles['multilevelSidebar--itemImageRing']}
                    src={
                      categoryImages[locale][
                        child.label.trim().toUpperCase()
                      ] ?? 'error'
                    }
                  />
                </div>
              )}

              <li style={{ borderWidth: depth === 0 ? '1px' : '0' }}>
                <span>{child.label}</span>
                {child?.children?.length > 0 && <ArrowDropRight />}
              </li>
            </Link>
          ))}
        </ul>
        {children.map((child, i) => {
          if (!Array.isArray(child?.children) || child?.children.length < 1) {
            return;
          }

          return renderItem(child.children, depth + 1, i, id, [
            ...categoryPath,
            { id, label: child.label, url: child.url },
          ]);
        })}
      </Fragment>
    );
  };

  return <>{renderItem(items)}</>;
};
